import axios from 'axios';
import { TickerSnapshotRequest, TickerSnapshotResponse } from '../types/ticker';
import { getSessionToken } from './sessionTokenService';
import { GetLatestAnalystViewsRequest, GetLatestAnalystViewsResponse, GetPast4QuartersAnalystViewsRequest, GetPast4QuartersAnalystViewsResponse } from '../types/analystViews';

// UserTickerPreferences interfaces
interface UserTickerPreferences {
  id: number;
  createdAt: string;
  updatedAt: string;
  appUserId: number;
  tickerBasicsId: number;
  technicalIndicatorsToHide?: string[];
  technicalDaysAgoToHide?: number[];
  fundamentalIndicatorsToHide?: string[];
  analystIndicatorsToHide?: string[];
  insiderIndicatorsToHide?: string[];
  prosConsIndicatorsToHide?: string[];
  performanceIndicatorsToHide?: string[];
}

interface CreateUserTickerPreferencesRequest {
  tickerBasicsId: number;
  technicalIndicatorsToHide?: string[];
  technicalDaysAgoToHide?: number[];
  fundamentalIndicatorsToHide?: string[];
  analystIndicatorsToHide?: string[];
  insiderIndicatorsToHide?: string[];
  prosConsIndicatorsToHide?: string[];
  performanceIndicatorsToHide?: string[];
}

interface UpdateUserTickerPreferencesRequest {
  technicalIndicatorsToHide?: string[];
  technicalDaysAgoToHide?: number[];
  fundamentalIndicatorsToHide?: string[];
  analystIndicatorsToHide?: string[];
  insiderIndicatorsToHide?: string[];
  prosConsIndicatorsToHide?: string[];
  performanceIndicatorsToHide?: string[];
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Create axios instance with default config
const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/v1`,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for session token and logging
api.interceptors.request.use(
  (config) => {
    // Add session token to all requests
    const sessionToken = getSessionToken();
    if (sessionToken) {
      config.headers['X-Session-Token'] = sessionToken;
    }
    console.log(`Making API request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`API response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error(`API response error: ${error.response?.status} ${error.config?.url}`, error.response?.data);
    return Promise.reject(error);
  }
);

export const tickerApi = {
  /**
   * Get latest performance returns for multiple tickers (unauthenticated)
   */
  async getTickerPerformance(request: { tickerBasicsIds?: number[]; tickerSymbols?: string[] }): Promise<any> {
    try {
      const response = await api.post('/ticker/performance', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get performance history (latest N records) for a ticker (unauthenticated)
   */
  async getTickerPerformanceHistory(request: { tickerBasicsId: number; limit?: number }): Promise<any> {
    try {
      const response = await api.post('/ticker/performance/history', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },
  /**
   * Get ticker snapshot data for multiple tickers
   */
  async getTickerSnapshot(request: TickerSnapshotRequest): Promise<TickerSnapshotResponse> {
    try {
      const response = await api.post<TickerSnapshotResponse>('/ticker/snapshot', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          // Safely extract error message, handling cases where message might be an object
          let errorMessage = 'Internal server error';
          if (error.response.data?.message) {
            if (typeof error.response.data.message === 'string') {
              errorMessage = error.response.data.message;
            } else if (typeof error.response.data.message === 'object') {
              // Try to extract a meaningful message from the error object
              errorMessage = JSON.stringify(error.response.data.message);
            }
          } else if (error.response.data && typeof error.response.data === 'object') {
            // If there's no message property, try to stringify the whole data object
            errorMessage = JSON.stringify(error.response.data);
          }
          throw new Error(`Server error: ${errorMessage}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get technical data for multiple tickers
   */
  async getTickerTechnical(request: { tickerBasicsIds: number[]; daysAgo?: number; daysAgoArray?: number[]; date?: string; category?: string; indicatorKey?: string; ownerHasProSubscription?: boolean }): Promise<any> {
    try {
      const response = await api.post('/ticker/technical', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Check sync status for tickers
   */
  async checkTickerSyncStatus(request: { ticker_symbols?: string[]; ticker_basics_ids?: number[] }): Promise<any> {
    try {
      const response = await api.post('/ticker/check-sync-status', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest prices for tickers
   */
  async getTickerLatestPrice(request: { ticker_symbols?: string[]; ticker_basics_ids?: number[] }): Promise<any> {
    try {
      const response = await api.post('/ticker/latest-price', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get moving average cross data for a range of days
  getMovingAverageCrossData: async (data: {
    ticker_symbols?: string[];
    ticker_basics_ids?: number[];
    start_days_ago: number;
    end_days_ago: number;
  }): Promise<any> => {
    try {
      const response = await api.post('/ticker/moving-average-cross', data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        if (error.response.status === 400) {
          throw new Error(`Bad request: ${error.response.data?.message || 'Invalid parameters'}`);
        }
        if (error.response.status === 429) {
          throw new Error(`Rate limit exceeded: ${error.response.data?.message || 'Too many requests'}`);
        }
        if (error.response.status >= 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get ticker basics for multiple tickers
   */
  async getTickerBasics(tickerSymbols: string[]): Promise<any> {
    try {
      const response = await api.post('/ticker/basics', { tickerSymbols: tickerSymbols });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest ended quarterly ratios for multiple tickers
   */
  async getLatestEndedQuarterlyRatios(tickerBasicsIds: number[]): Promise<any> {
    try {
      const response = await api.get(`/ticker/ratios/quarterly/latest-ended?ids=${tickerBasicsIds.join(',')}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest quarterly financials for multiple tickers
   */
  async getLatestQuarterlyFinancials(tickerBasicsIds: number[]): Promise<any> {
    try {
      const response = await api.get(`/ticker/financials/latest?ids=${tickerBasicsIds.join(',')}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get current quarterly ratios for multiple tickers
   */
  async getCurrentQuarterlyRatios(tickerBasicsIds: number[], ownerHasProSubscription?: boolean): Promise<any> {
    try {
      let url = `/ticker/ratios/quarterly/current?ids=${tickerBasicsIds.join(',')}`;
      if (ownerHasProSubscription !== undefined) {
        url += `&ownerHasProSubscription=${ownerHasProSubscription}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest annual ratios for multiple tickers
   */
  async getLatestAnnualRatios(tickerBasicsIds: number[]): Promise<any> {
    try {
      const response = await api.get(`/ticker/ratios/annual/latest?ids=${tickerBasicsIds.join(',')}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest annual financials for multiple tickers
   */
  async getLatestAnnualFinancials(tickerBasicsIds: number[]): Promise<any> {
    try {
      const response = await api.get(`/ticker/financials/annual/latest?ids=${tickerBasicsIds.join(',')}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get latest ended quarterly ratios with comparisons
  async getLatestEndedQuarterlyRatiosWithComparisons(tickerBasicsIds: number[], ownerHasProSubscription?: boolean): Promise<any> {
    try {
      let url = `/ticker/ratios/quarterly/latest-ended-and-comparisons?ids=${tickerBasicsIds.join(',')}`;
      if (ownerHasProSubscription !== undefined) {
        url += `&ownerHasProSubscription=${ownerHasProSubscription}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('HTTPS required')) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.message.includes('Rate limit exceeded')) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.message.includes('Server error')) {
          throw new Error(`Server error: ${error.message}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get latest ended quarterly financials with comparisons
  async getLatestEndedQuarterlyFinancialsWithComparisons(tickerBasicsIds: number[]): Promise<any> {
    try {
      const response = await api.get(`/ticker/financials/latest-and-comparisons?ids=${tickerBasicsIds.join(',')}`);
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('HTTPS required')) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.message.includes('Rate limit exceeded')) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.message.includes('Server error')) {
          throw new Error(`Server error: ${error.message}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get latest annual ratios with comparisons
  async getLatestAnnualRatiosWithComparisons(tickerBasicsIds: number[], ownerHasProSubscription?: boolean): Promise<{ success: boolean; data: any[]; message: string }> {
    try {
      const ids = tickerBasicsIds.join(',');
      let url = `/ticker/ratios/annual/compare?ids=${ids}`;
      if (ownerHasProSubscription !== undefined) {
        url += `&ownerHasProSubscription=${ownerHasProSubscription}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        if (status >= 500) {
          throw new Error(`Server error: ${data?.message || 'Internal server error'}`);
        }
        if (status >= 400) {
          throw new Error(`Request failed: ${data?.message || 'Bad request'}`);
        }
        throw new Error(`Request failed: ${data?.message || 'Unknown error'}`);
      }
      if (error.request) {
        throw new Error('Network error: Unable to connect to server');
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get latest annual financials with comparisons
  async getLatestAnnualFinancialsWithComparisons(tickerBasicsIds: number[]): Promise<{ success: boolean; data: any[]; message: string }> {
    try {
      const ids = tickerBasicsIds.join(',');
      const response = await api.get(`/ticker/financials/annual/compare?ids=${ids}`);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        if (status >= 500) {
          throw new Error(`Server error: ${data?.message || 'Internal server error'}`);
        }
        if (status >= 400) {
          throw new Error(`Request failed: ${data?.message || 'Bad request'}`);
        }
        throw new Error(`Request failed: ${data?.message || 'Unknown error'}`);
      }
      if (error.request) {
        throw new Error('Network error: Unable to connect to server');
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest analyst views for multiple tickers
   */
  async getLatestAnalystViews(request: GetLatestAnalystViewsRequest): Promise<GetLatestAnalystViewsResponse> {
    try {
      const response = await api.post<GetLatestAnalystViewsResponse>('/ticker/latest-analyst-views', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Please check your authentication');
        }
        if (error.response?.status === 404) {
          throw new Error('Analyst views not found');
        }
        if (error.response?.status === 500) {
          throw new Error('Server error: Please try again later');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get daily price data for a specific ticker with pagination
   */
  async getTickerDailyPrices(tickerSymbol: string, page: number = 0, limit: number = 50, timeframe?: string, sortColumn?: string, sortDirection?: string): Promise<{ data: any; meta: any }> {
    try {
      const requestBody: any = {
        ticker_symbol: tickerSymbol.toUpperCase(),
        page: page,
        limit: limit
      };
      
      if (timeframe) {
        requestBody.timeframe = timeframe;
      }
      
      if (sortColumn) {
        requestBody.sort_column = sortColumn;
      }
      
      if (sortDirection) {
        requestBody.sort_direction = sortDirection;
      }
      
      const response = await api.post('/ticker/daily-prices', requestBody);
      
      return { 
        data: response.data.data,
        meta: response.data.meta
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Ticker not found');
        }
        if (error.response?.status === 500) {
          throw new Error('Server error: Please try again later');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get batch daily price data for multiple tickers
   */
  async getBatchTickerDailyPrices(tickerSymbols: string[], recordsPerTicker: number = 2): Promise<{ data: any; meta: any }> {
    try {
      const requestBody = {
        ticker_symbols: tickerSymbols.map(symbol => symbol.toUpperCase()),
        records_per_ticker: recordsPerTicker
      };
      
      const response = await api.post('/ticker/batch-daily-prices', requestBody);
      
      return { 
        data: response.data.data,
        meta: response.data.meta
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('No tickers found for the provided symbols');
        }
        if (error.response?.status === 500) {
          throw new Error('Server error: Please try again later');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get past 2 years quarterly financials for a specific ticker
  async getPast2YearsQuarterlyFinancials(tickerBasicsId: number): Promise<any> {
    try {
      const response = await api.get(`/ticker/financials/past-2-years-quarterly/${tickerBasicsId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching past 2 years quarterly financials:', error);
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get past 2 years quarterly ratios for a specific ticker
  async getPast2YearsQuarterlyRatios(tickerBasicsId: number): Promise<any> {
    try {
      const response = await api.get(`/ticker/ratios/past-2-years-quarterly/${tickerBasicsId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching past 2 years quarterly ratios:', error);
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get past 5 years annual financials for a specific ticker
  async getPast5YearsAnnualFinancials(tickerBasicsId: number): Promise<any> {
    try {
      const response = await api.get(`/ticker/financials/past-5-years-annual/${tickerBasicsId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching past 5 years annual financials:', error);
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Get past 5 years annual ratios for a specific ticker
  async getPast5YearsAnnualRatios(tickerBasicsId: number): Promise<any> {
    try {
      const response = await api.get(`/ticker/ratios/past-5-years-annual/${tickerBasicsId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching past 5 years annual ratios:', error);
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get past 4 quarters analyst views for a single ticker
   */
  async getPast4QuartersAnalystViews(request: GetPast4QuartersAnalystViewsRequest): Promise<GetPast4QuartersAnalystViewsResponse> {
    try {
      const response = await api.post<GetPast4QuartersAnalystViewsResponse>('/ticker/past-4-quarters-analyst-views', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Please check your authentication');
        }
        if (error.response?.status === 404) {
          throw new Error('Ticker not found');
        }
        if (error.response?.status === 429) {
          throw new Error('Rate limit exceeded: Please try again later');
        }
        if (error.response?.status && error.response.status >= 500) {
          throw new Error('Server error: Please try again later');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // UserTickerPreferences methods
  async createUserTickerPreferences(request: CreateUserTickerPreferencesRequest): Promise<ApiResponse<UserTickerPreferences>> {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await api.post<ApiResponse<UserTickerPreferences>>('/user-ticker/preferences', request, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Please check your authentication');
        }
        if (error.response?.status === 404) {
          throw new Error('Ticker not found');
        }
        if (error.response?.status === 409) {
          throw new Error('Preferences already exist for this ticker');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  async updateUserTickerPreferences(tickerBasicsId: number, request: UpdateUserTickerPreferencesRequest): Promise<ApiResponse<UserTickerPreferences>> {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await api.put<ApiResponse<UserTickerPreferences>>(`/user-ticker/preferences/${tickerBasicsId}`, request, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Please check your authentication');
        }
        if (error.response?.status === 404) {
          throw new Error('Preferences not found');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  async getUserTickerPreferences(tickerBasicsId: number): Promise<ApiResponse<UserTickerPreferences>> {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await api.get<ApiResponse<UserTickerPreferences>>(`/user-ticker/preferences/${tickerBasicsId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Please check your authentication');
        }
        if (error.response?.status === 404) {
          throw new Error('Preferences not found');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  async deleteUserTickerPreferences(tickerBasicsId: number): Promise<ApiResponse<void>> {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await api.delete<ApiResponse<void>>(`/user-ticker/preferences/${tickerBasicsId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Please check your authentication');
        }
        if (error.response?.status === 404) {
          throw new Error('Preferences not found');
        }
        throw new Error(`API error: ${error.response?.data?.message || error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Landing page cached endpoints - for default tickers only
   */
  landingCache: {
    /**
     * Get cached daily snapshot data for default tickers
     */
    async getCachedDaily(request: TickerSnapshotRequest): Promise<TickerSnapshotResponse> {
      try {
        const response = await api.post<TickerSnapshotResponse>('/landing-cache/daily', request);
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 400) {
            throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
          }
          if (error.response?.status === 500) {
            throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
          }
          throw new Error(`API error: ${error.response?.data?.message || error.message}`);
        }
        throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },

    /**
     * Get complete cached technical data for default tickers
     */
    async getCachedCompleteTechnicals(): Promise<any> {
      try {
        const response = await api.post('/landing-cache/technicals/complete', {});
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 500) {
            throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
          }
          throw new Error(`API error: ${error.response?.data?.message || error.message}`);
        }
        throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },

    /**
     * Get cached fundamentals data for default tickers
     */
    async getCachedFundamentals(timeframe: 'quarterly' | 'annual' = 'quarterly'): Promise<any> {
      try {
        const response = await api.post('/landing-cache/fundamentals', { timeframe });
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 500) {
            throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
          }
          throw new Error(`API error: ${error.response?.data?.message || error.message}`);
        }
        throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },

    /**
     * Get complete cached analyst views data for default tickers
     */
    async getCachedCompleteAnalystViews(): Promise<any> {
      try {
        const response = await api.post('/landing-cache/analyst-views/complete', {});
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 500) {
            throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
          }
          throw new Error(`API error: ${error.response?.data?.message || error.message}`);
        }
        throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },

    /**
     * Get complete cached pros/cons data for default tickers
     */
    async getCachedCompleteProsCons(): Promise<any> {
      try {
        const response = await api.post('/landing-cache/pros-cons/complete', {});
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 500) {
            throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
          }
          throw new Error(`API error: ${error.response?.data?.message || error.message}`);
        }
        throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
  },

  /**
   * Check insider sync status for tickers
   */
  async checkInsiderSyncStatus(request: { ticker_basics_ids: number[] }): Promise<any> {
    try {
      const response = await api.post('/ticker/check-insider-sync-status', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get insider transactions by date range
   */
  async getInsiderTransactionsByDateRange(request: { tickerBasicsIds: number[]; startDate: string; endDate: string; ownerHasProSubscription?: boolean }): Promise<any> {
    try {
      const response = await api.post('/ticker/insider-transactions/by-date-range', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Check pros/cons sync status for tickers
   */
  async checkProsAndConsSyncStatus(request: { ticker_basics_ids: number[] }): Promise<any> {
    try {
      const response = await api.post('/ticker/check-pros-cons-sync-status', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Get latest pros/cons for multiple tickers
   */
  async getLastProsAndCons(request: { tickerBasicsIds: number[]; ownerHasProSubscription?: boolean }): Promise<any> {
    try {
      const response = await api.post('/ticker/pros-cons/last', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /** Backwards-compatible alias. */
  async getMonthlyProsAndCons(request: { tickerBasicsIds: number[]; ownerHasProSubscription?: boolean }): Promise<any> {
    return this.getLastProsAndCons(request);
  },

  async getProsAndConsHistory(request: { tickerBasicsId: number; limit?: number; ownerHasProSubscription?: boolean }): Promise<any> {
    try {
      const response = await api.post('/ticker/pros-cons/history', request);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request'}`);
        }
        if (error.response?.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (error.response?.status === 403) {
          throw new Error('Forbidden: HTTPS required');
        }
        if (error.response?.status === 429) {
          throw new Error('Too many requests: Rate limit exceeded');
        }
        if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response.data?.message || 'Internal server error'}`);
        }
        throw new Error(`Request failed: ${error.message}`);
      }
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },
};

export default tickerApi;
export type { 
  UserTickerPreferences, 
  CreateUserTickerPreferencesRequest, 
  UpdateUserTickerPreferencesRequest 
};
